import React, { useState, useEffect, useId, useRef } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Card } from '@/components/Card';
import { UserRole, type CreateUserRequest, type Company } from '@/types/api';
import { userService } from '@/services/userService';
import { validateEmail, validatePassword } from '@/utils/validation';

/**
 * User Creation Form Schema with Zod Validation
 */
const createUserSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(254, "Email address is too long")
    .refine((email) => validateEmail(email), {
      message: "Please enter a valid email address",
    }),
  
  password: z
    .string()
    .min(8, "Password must be at least 8 characters long")
    .max(128, "Password is too long")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"),
  
  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),
  
  role: z.nativeEnum(UserRole, {
    errorMap: () => ({ message: "Please select a valid role" }),
  }),
  
  companyId: z
    .string()
    .optional()
    .nullable(),
  
  department: z
    .string()
    .max(100, "Department name is too long")
    .optional(),
  
  jobTitle: z
    .string()
    .max(100, "Job title is too long")
    .optional(),
  
  phoneNumber: z
    .string()
    .regex(/^[\+]?[(]?[\d\s\-\(\)]{10,20}$/, "Please enter a valid phone number")
    .optional()
    .or(z.literal("")),
  
  sendWelcomeEmail: z.boolean().default(true),
  
  isActive: z.boolean().default(true),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
  companies?: Company[];
}

/**
 * Create User Modal Component
 * 
 * Comprehensive form for creating new users with validation,
 * company selection, and role management.
 */
export function CreateUserModal({ isOpen, onClose, onSuccess, companies = [] }: CreateUserModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
    clearErrors,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: UserRole.Agent,
      companyId: '',
      department: '',
      jobTitle: '',
      phoneNumber: '',
      sendWelcomeEmail: true,
      isActive: true,
    },
    mode: 'onChange',
  });

  const watchedEmail = watch('email');
  const watchedRole = watch('role');

  // Generate secure password
  const generatePassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const specials = '@$!%*?&';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += specials[Math.floor(Math.random() * specials.length)];
    
    // Fill the rest randomly
    const allChars = uppercase + lowercase + numbers + specials;
    for (let i = 4; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    const passwordArray = password.split('');
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
    }
    
    const finalPassword = passwordArray.join('');
    setGeneratedPassword(finalPassword);
    setValue('password', finalPassword);
    setValue('confirmPassword', finalPassword);
    clearErrors(['password', 'confirmPassword']);
  };

  // Check if email exists
  useEffect(() => {
    const checkEmail = async () => {
      if (!watchedEmail || !validateEmail(watchedEmail) || watchedEmail.length < 3) {
        setEmailChecked(false);
        setEmailExists(false);
        return;
      }

      try {
        setEmailChecked(false);
        const response = await userService.checkUserExists(watchedEmail);
        
        if (response.success) {
          setEmailExists(response.data.exists);
          setEmailChecked(true);
        }
      } catch (error) {
        console.error('Email check failed:', error);
        setEmailChecked(false);
        setEmailExists(false);
      }
    };

    const timeoutId = setTimeout(checkEmail, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedEmail]);

  // Handle form submission
  const onSubmit = async (data: CreateUserFormData) => {
    if (emailExists) {
      setError('A user with this email address already exists.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const userData: CreateUserRequest = {
        email: data.email.toLowerCase().trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        password: data.password,
        role: data.role,
        companyId: data.companyId || undefined,
        department: data.department?.trim() || undefined,
        jobTitle: data.jobTitle?.trim() || undefined,
        phone: data.phoneNumber?.trim() || undefined,
        sendWelcomeEmail: data.sendWelcomeEmail,
        isActive: data.isActive,
      };

      const response = await userService.createUser(userData);

      if (response.success) {
        onSuccess(response.data);
        reset();
        setGeneratedPassword('');
        onClose();
      } else {
        setError(response.message || 'Failed to create user. Please try again.');
      }
    } catch (error: any) {
      console.error('Create user error:', error);
      
      // Extract the most specific error message available
      let errorMessage = 'An error occurred while creating the user.';
      
      if (error.response?.data) {
        // Try different possible error message locations
        errorMessage = error.response.data.message || 
                      error.response.data.error || 
                      error.response.data.title ||
                      (error.response.data.errors && Object.values(error.response.data.errors).flat().join(', ')) ||
                      errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      reset();
      setError(null);
      setEmailChecked(false);
      setEmailExists(false);
      setGeneratedPassword('');
      onClose();
    }
  };

  // BugHerd #132: dialog semantics and focus management without restructuring markup.
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalA11y({ isOpen, onClose: handleClose, containerRef: dialogRef });

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 focus:outline-none"
          data-testid="create-user-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 id={titleId} className="text-lg font-medium leading-6 text-gray-900">
              Create New User
            </h3>
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              onClick={handleClose}
              disabled={isLoading}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Error Display */}
            {error && (
              <div className="rounded-md bg-red-50 p-4" data-testid="error-message" role="alert">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className="mt-1"
                  placeholder="Enter first name"
                  data-testid="user-form-firstname"
                  {...register('firstName')}
                  error={errors.firstName?.message}
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className="mt-1"
                  placeholder="Enter last name"
                  data-testid="user-form-lastname"
                  {...register('lastName')}
                  error={errors.lastName?.message}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <div className="mt-1 relative">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter email address"
                  data-testid="user-form-email"
                  {...register('email')}
                  error={errors.email?.message}
                />
                {watchedEmail && emailChecked && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {emailExists ? (
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              {emailExists && (
                <p className="mt-1 text-sm text-red-600">A user with this email already exists.</p>
              )}
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <div className="mt-1 relative">
                  <Input
                    id="password"
                    type={passwordVisible ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Enter password"
                    data-testid="user-form-password"
                    {...register('password')}
                    error={errors.password?.message}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {passwordVisible ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      )}
                    </svg>
                  </button>
                </div>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generatePassword}
                    className="w-full"
                    data-testid="generate-password-button"
                  >
                    Generate Secure Password
                  </Button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password *
                </label>
                <div className="mt-1 relative">
                  <Input
                    id="confirmPassword"
                    type={confirmPasswordVisible ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Confirm password"
                    data-testid="user-form-confirm-password"
                    {...register('confirmPassword')}
                    error={errors.confirmPassword?.message}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {confirmPasswordVisible ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Generated Password Display */}
            {generatedPassword && (
              <div className="p-4 bg-blue-50 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Generated Password</h3>
                    <p className="mt-1 text-sm text-blue-700 font-mono break-all">
                      {generatedPassword}
                    </p>
                    <p className="mt-1 text-xs text-blue-600">
                      Please save this password securely. It will be sent to the user if welcome email is enabled.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role and Company */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role *
                </label>
                <select
                  id="role"
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  data-testid="user-form-role"
                  {...register('role', { 
                    setValueAs: (value) => parseInt(value) 
                  })}
                  defaultValue={UserRole.Agent}
                >
                  <option value={UserRole.EndUser}>End User</option>
                  <option value={UserRole.Agent}>Support Agent</option>
                  <option value={UserRole.Manager}>Manager</option>
                  <option value={UserRole.Admin}>Administrator</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                  Company
                </label>
                <select
                  id="companyId"
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  {...register('companyId')}
                >
                  <option value="">No Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {errors.companyId && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyId.message}</p>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                  Department
                </label>
                <Input
                  id="department"
                  type="text"
                  className="mt-1"
                  placeholder="e.g., Engineering, Sales"
                  data-testid="user-form-department"
                  {...register('department')}
                  error={errors.department?.message}
                />
              </div>

              <div>
                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
                  Job Title
                </label>
                <Input
                  id="jobTitle"
                  type="text"
                  className="mt-1"
                  placeholder="e.g., Senior Developer"
                  data-testid="user-form-job-title"
                  {...register('jobTitle')}
                  error={errors.jobTitle?.message}
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <Input
                id="phoneNumber"
                type="tel"
                autoComplete="tel"
                className="mt-1"
                placeholder="+1 (555) 123-4567"
                data-testid="user-form-phone"
                {...register('phoneNumber')}
                error={errors.phoneNumber?.message}
              />
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="sendWelcomeEmail"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    {...register('sendWelcomeEmail')}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="sendWelcomeEmail" className="font-medium text-gray-700">
                    Send welcome email
                  </label>
                  <p className="text-gray-500">
                    Send an email with login instructions and temporary password.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="isActive"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    {...register('isActive')}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isActive" className="font-medium text-gray-700">
                    User is active
                  </label>
                  <p className="text-gray-500">
                    Allow user to log in and access the system.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                data-testid="cancel-user-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={!isValid || emailExists || isLoading}
                data-testid="save-user-button"
              >
                Create User
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}